import { plainToClass } from 'class-transformer';
import qs from 'qs';
import { hybrisProxyHttp, hybrisHttp, hybrisFormHttp } from './http';
import {
    PostcodeModel,
    DeliveryTypes,
    HybrisFieldTypes,
    HybrisCustomerDetailsModel,
    UserExistsModel,
    CredentialsModel,
    PlacedOrderModel,
    OrderPayload,
    convertHybrisAddressResponseToAddressModel,
    AddressModel,
    AddToCartItemModel,
    AddToCartResultModel
} from 'models';
import { BasketErrorModel, BasketHybrisFormErrorModel, BasketApiErrorModel, isApiError, BasketPaymentErrorModel, DEFAULT_ERROR_TEXT } from 'models/Errors';
import {
    BasketDetailsModel,
    BasketInfoModel,
    EntryModel,
    BasketAddonModel,
    BasketSessionModel,
    BasketCareKitAddonModel
} from 'models/Basket';
import { userHelper, channelHelper, errorToBasketErrorModel } from 'helpers';
import { PaypalSuccessfulPaymentResponseModel, PaymentDetailsModel } from 'models/Payment';
import { WRONG_PASSWORD_WARNING_LINES, ACCOUNT_NOT_ACTIVATED_WARNING_LINES } from '../pages/DeliveryDetailsPage/DeliveryDetailsForm/deliveryDetailsAdditionalData';
import { AxiosResponse } from 'axios';
import { FinanceApplicationStatusModel } from 'models/FinanceApplicationStatusModel';

const DATA_VALIDATION_ERROR_STATUS_CODE = 422;
const POSTCODE_UNKNOWN_ERROR_TYPE = 'PostcodeLookupException';
export const PAYPAL_DECLINED_ERROR_CODE = 'INSTRUMENT_DECLINED';

export class HybrisRepository {
    public async getBasketInfo(username: string | null): Promise<BasketInfoModel | null> {
        try {
            const defaultReturnValue = null;
            const response = username ? await hybrisProxyHttp.get(`/api/users/${username}/carts`, {
                params: {
                    fields: HybrisFieldTypes.Basic,
                    chl: channelHelper.getChannel()
                },
                headers: this.getHybrisAuthHeaders()
            }) : defaultReturnValue;

            return response?.data?.carts ? plainToClass(BasketInfoModel, response.data.carts[0]) : Promise.resolve(defaultReturnValue);
        } catch (error) {
            const errorSource = 'hybris.repository:getBasketInfo';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async createBasket(username: string | null): Promise<BasketInfoModel> {
        try {
            const response = await hybrisProxyHttp.post(`/api/users/${this.createUserNameForUrl(username)}/carts`, undefined, {
                params: {
                    chl: channelHelper.getChannel()
                },
                headers: this.getHybrisAuthHeaders()
            });

            return plainToClass(BasketInfoModel, response.data);
        } catch (error) {
            const errorSource = 'hybris.repository:createBasket';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async placeIlpOrderWithoutDeposit(username: string, basketId: string, payload: any): Promise<any> {
        try {
            let response: AxiosResponse;

            response = await hybrisProxyHttp.post(`/api/users/${this.createUserNameForUrl(username)}/carts/${basketId}/ilp_order`, payload, {
                params: {
                    channel: channelHelper.getChannel()
                },
                headers: this.getHybrisAuthHeaders()
            });

            return plainToClass(BasketInfoModel, response.data);
        } catch (error) {
            const errorSource = 'hybris.repository:placeIlpOrderWithoutDeposit';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async updateBasketIlpData(username: string, basketId: string, payload: any): Promise<any> {
        try {
            let response: AxiosResponse;

            response = await hybrisProxyHttp.put(`/api/users/${this.createUserNameForUrl(username)}/carts/${basketId}/ilp_order`, payload, {
                params: {
                    channel: channelHelper.getChannel()
                },
                headers: this.getHybrisAuthHeaders()
            });

            return plainToClass(BasketInfoModel, response.data);
        } catch (error) {
            const errorSource = 'hybris.repository:updateBasketIlpData';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async getBasketDetailsBySessionId(username: string | null, sessionId: string): Promise<BasketDetailsModel> {
        try {
            const response = await hybrisProxyHttp.get<BasketDetailsModel>(`/api/users/${this.createUserNameForUrl(username)}/carts`, {
                params: {
                    fields: HybrisFieldTypes.Full,
                    sessionId,
                    chl: channelHelper.getChannel()
                },
                headers: this.getHybrisAuthHeaders()
            });

            return plainToClass(BasketDetailsModel, response.data);
        } catch (error) {
            const errorSource = 'hybris.repository:getBasketDetailsBySessionId';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async getBasketDetailsByBasketId(username: string | null, basketId: string): Promise<BasketDetailsModel> {
        try {
            const response = await hybrisProxyHttp.get(`/api/users/${this.createUserNameForUrl(username)}/carts/${basketId}`, {
                params: {
                    fields: HybrisFieldTypes.Full,
                    chl: channelHelper.getChannel()
                },
                headers: this.getHybrisAuthHeaders()
            });

            return plainToClass(BasketDetailsModel, response.data);
        } catch (error) {
            const errorSource = 'hybris.repository:getBasketDetailsByBasketId';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async addProduct(username: string | null, basketId: string, code: string, quantity: number, sofaglideParentCode?: string): Promise<EntryModel> {
        try {
            const url = `/api/users/${this.createUserNameForUrl(username)}/carts/${basketId}/entries`;

            const response = await hybrisProxyHttp.post(url, undefined, {
                params: {
                    code,
                    qty: quantity,
                    sofaGlideParentCode: sofaglideParentCode,
                    ch1: channelHelper.getChannel()
                },
                headers: this.getHybrisAuthHeaders()
            });

            return plainToClass(EntryModel, response.data);
        } catch (error) {
            const errorSource = 'hybris.repository:addProduct';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async updateBasketItemQuantity(username: string | null, basketId: string, entryNumber: number, quantity: number): Promise<EntryModel> {
        try {
            const response = await hybrisProxyHttp.put(`/api/users/${this.createUserNameForUrl(username)}/carts/${basketId}/entries/${entryNumber}`, {
                quantity: quantity
            }, {
                params: {
                    chl: channelHelper.getChannel()
                },
                headers: this.getHybrisAuthHeaders()
            });

            return plainToClass(EntryModel, response.data);
        } catch (error) {
            const errorSource = 'hybris.repository:updateBasketItemQuantity';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async updateSofashieldQuantity(username: string | null, basketId: string, entryNumber: number, quantity: number, sofashieldQuantity: number): Promise<EntryModel> {
        try {
            const response = await hybrisProxyHttp.put(`/api/users/${this.createUserNameForUrl(username)}/carts/${basketId}/entries/${entryNumber}`,
                undefined,
                {
                    params: {
                        qty: quantity,
                        sofaShieldQuantity: sofashieldQuantity,
                        chl: channelHelper.getChannel()
                    },
                    headers: this.getHybrisAuthHeaders()
                });

            return plainToClass(EntryModel, response.data);
        } catch (error) {
            const errorSource = 'hybris.repository:updateSofashieldQuantity';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async removeBasketItem(username: string | null, basketId: string, entryNumber: number, sofaglideParentCode?: string) {
        try {
            const url = `/api/users/${this.createUserNameForUrl(username)}/carts/${basketId}/entries/${entryNumber}`;

            await hybrisProxyHttp.delete(url, {
                params: {
                    sofaGlideParentCode: sofaglideParentCode,
                    chl: channelHelper.getChannel()
                },
                headers: this.getHybrisAuthHeaders()
            });
        } catch (error) {
            const errorSource = 'hybris.repository:removeBasketItem';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async calculateDeliveryCost(postcode: PostcodeModel, username: string | null, basketId: string): Promise<void> {
        try {
            await hybrisProxyHttp.post(`/api/users/${this.createUserNameForUrl(username)}/carts/${basketId}/postalCode`, null, {
                params: {
                    postalCode: postcode.code
                },
                headers: this.getHybrisAuthHeaders()
            });

        } catch (error) {
            const errorSource = 'hybris.repository:calculateDeliveryCost';
            if (isApiError(error)) {
                const apiError = new BasketApiErrorModel(error, errorSource);

                let errors = this.getHybrisErrorsListFormResponse(error, 'calculateDeliveryCost', apiError);

                // if postcode does not exists (for example: A11AA) then api fails with 503 instead of 400 with error list
                if (errors.length === 0 && error.response?.data?.type === POSTCODE_UNKNOWN_ERROR_TYPE) {
                    errors = [new BasketHybrisFormErrorModel(DEFAULT_ERROR_TEXT, 'calculateDeliveryCost', 'ValidationError', 'postalCode', 'invalid', apiError)];
                }

                throw errors.length ? errors : [apiError];
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async setDeliveryType(deliveryType: DeliveryTypes, username: string | null, basketId: string): Promise<void> {
        try {
            await hybrisProxyHttp.post(`/api/users/${this.createUserNameForUrl(username)}/carts/${basketId}/deliveryoptions`, null, {
                params: {
                    code: deliveryType
                },
                headers: this.getHybrisAuthHeaders()
            });
        } catch (error) {
            const errorSource = 'hybris.repository:setDeliveryType';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async getPaymentDetails(username: string | null, sessionId: string): Promise<PaymentDetailsModel> {
        try {
            const response = await hybrisProxyHttp.get<PaymentDetailsModel>(`/payment/api/selections/users/${this.createUserNameForUrl(username)}/sessions/${sessionId}`, {
                params: {
                    chl: channelHelper.getChannel()
                },
                headers: this.getHybrisAuthHeaders()
            });

            let res = plainToClass(PaymentDetailsModel, response.data);

            if (response.data && response.data.total === null) {
                res.isEmptyModel = true;
            }

            return res;
        } catch (error) {
            const errorSource = 'hybris.repository:getPaymentDetails';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async savePaymentDetails(username: string | null, sessionId: string, paymentDetails: PaymentDetailsModel): Promise<void> {
        try {
            await hybrisProxyHttp.post(`/payment/api/selections/users/${this.createUserNameForUrl(username)}/sessions/${sessionId}`, {
                ...paymentDetails
            }, {
                params: {
                    chl: channelHelper.getChannel()
                },
                headers: this.getHybrisAuthHeaders()
            });
        } catch (error) {
            const errorSource = 'hybris.repository:savePaymentDetails';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async getSessionByBasketId(username: string | null, basketId: string): Promise<BasketSessionModel> {
        try {
            const response = await hybrisProxyHttp.get(`/api/users/${this.createUserNameForUrl(username)}/sessions`, {
                params: {
                    cartGuid: basketId,
                    chl: channelHelper.getChannel()
                },
                headers: this.getHybrisAuthHeaders()
            });

            return plainToClass(BasketSessionModel, response.data);
        } catch (error) {
            const errorSource = 'hybris.repository:getSessionByBasketId';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async getBasketSofashieldAddons(username: string | null, basketId: string): Promise<BasketAddonModel[]> {
        try {
            return this.getBasketAddons(username, basketId, 'sofaShields');
        } catch (error) {
            const errorSource = 'hybris.repository:getBasketSofashieldAddons';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async getBasketCareKitsAddons(username: string | null, basketId: string): Promise<BasketAddonModel[]> {
        try {
            return this.getBasketAddons(username, basketId, 'careKits');
        } catch (error) {
            const errorSource = 'hybris.repository:getBasketCareKitsAddons';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async getCustomerDetails(username: string): Promise<HybrisCustomerDetailsModel> {
        try {
            // parameter timestamp is included to avoid caching request (not required parameter)
            const response = await hybrisHttp.get<CustomerDetailsResponseModel>(`/v2/cmsSofaworksSite/users/${username.toLowerCase()}`, {
                params: {
                    fields: HybrisFieldTypes.Full,
                    chl: channelHelper.getChannel(),
                    timestamp: new Date().getTime()
                },
                headers: this.getHybrisAuthHeaders()
            });

            return this.parseCustomerDetails(response.data);
        } catch (error) {
            const errorSource = 'hybris.repository:getCustomerDetails';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async updateCustomerDeliveryDetails(username: string, basketId: string, customerDetails: HybrisCustomerDetailsModel, address: AddressModel): Promise<void> {
        try {
            let requestData = this.createUpdateCustumerDetailsRequestModel(customerDetails, address, false);

            await hybrisHttp.post<CustomerDetailsResponseModel>(`/v2/cmsSofaworksSite/users/${username.toLowerCase()}/carts/${basketId}/addresses/delivery/`,
                requestData,
                {
                    params: {
                        chl: channelHelper.getChannel()
                    },
                    headers: this.getHybrisAuthHeaders()
                });
        } catch (error) {
            const errorSource = 'hybris.repository:updateCustomerDeliveryDetails';
            if (isApiError(error)) {
                const apiError = new BasketApiErrorModel(error, errorSource);

                let errors = this.getHybrisErrorsListFormResponse(error, 'deliveryDetails', apiError);

                // if postcode does not exists (for example: A11AA) then api fails with 503 instead of 400 with error list
                if (errors.length === 0 && error.response?.data?.type === POSTCODE_UNKNOWN_ERROR_TYPE) {
                    errors = [new BasketHybrisFormErrorModel(DEFAULT_ERROR_TEXT, 'deliveryDetails', 'ValidationError', 'postalCode', 'invalid', apiError)];
                }

                throw errors.length ? errors : [apiError];
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async updateCustomerBillingDetails(username: string, basketId: string, customerDetails: HybrisCustomerDetailsModel, address: AddressModel): Promise<void> {
        try {
            let requestData = this.createUpdateCustumerDetailsRequestModel(customerDetails, address, true);

            await hybrisHttp.post<CustomerDetailsResponseModel>(`/v2/cmsSofaworksSite/users/${username.toLowerCase()}/carts/${basketId}/addresses/payment/`,
                requestData,
                {
                    params: {
                        chl: channelHelper.getChannel()
                    },
                    headers: this.getHybrisAuthHeaders()
                });
        } catch (error) {
            const errorSource = 'hybris.repository:updateCustomerBillingDetails';
            if (isApiError(error)) {
                const apiError = new BasketApiErrorModel(error, errorSource);

                let errors = this.getHybrisErrorsListFormResponse(error, 'billingDetails', apiError);

                // if postcode does not exists (for example: A11AA) then api fails with 503 instead of 400 with error list
                if (errors.length === 0 && error.response?.data?.type === POSTCODE_UNKNOWN_ERROR_TYPE) {
                    errors = [new BasketHybrisFormErrorModel(DEFAULT_ERROR_TEXT, 'billingDetails', 'ValidationError', 'postalCode', 'invalid', apiError)];
                }

                throw errors.length ? errors : [apiError];
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async saveEca(username: string | null, basketId: string, date: number) {
        try {
            await hybrisProxyHttp.post(`/api/users/${this.createUserNameForUrl(username)}/carts/${basketId}/eca`, undefined, {
                params: {
                    date,
                    chl: channelHelper.getChannel()
                },
                headers: this.getHybrisAuthHeaders()
            });
        } catch (error) {
            const errorSource = 'hybris.repository:saveEca';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async checkEmail(email: string): Promise<UserExistsModel> {
        try {
            const response = await hybrisHttp.get<UserExistsModel>(`/v2/cmsSofaworksSite/users/anonymous/exists/${email}`, {
                params: {
                    chl: channelHelper.getChannel()
                },
                headers: this.getHybrisAuthHeaders()
            });

            return plainToClass(UserExistsModel, response.data);
        } catch (error) {
            const errorSource = 'hybris.repository:checkEmail';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async getCredentials(email: string, password: string): Promise<CredentialsModel> {
        try {
            const data = {
                username: email.toLowerCase(),
                password: password
            };

            const result = await hybrisFormHttp.post(
                '/oauth/token',
                qs.stringify(data),
                {
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    params: {
                        client_id: 'commerce_app',
                        client_secret: 'commerce_app_secret',
                        grant_type: 'password',
                        pointOfService: 'INTERNET',
                        channel: channelHelper.getChannel()
                    }
                }
            );

            return plainToClass(CredentialsModel, result.data);
        } catch (error) {
            const errorSource = 'hybris.repository:getCredentials';
            if (isApiError(error)) {
                const apiError = new BasketApiErrorModel(error, errorSource);

                const reWords = {
                    'User is disabled': ACCOUNT_NOT_ACTIVATED_WARNING_LINES.join(' '),
                    'Bad credentials': WRONG_PASSWORD_WARNING_LINES.join(' ')
                };
                if (error.response?.data?.errors?.length && reWords[error.response.data.errors[0].message]) {
                    throw new BasketErrorModel(reWords[error.response.data.errors[0].message], undefined, errorSource, undefined, apiError);
                }

                throw new BasketErrorModel(DEFAULT_ERROR_TEXT, undefined, errorSource, undefined, apiError);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async transferBasket(username: string, basketId: string): Promise<BasketDetailsModel> {
        try {
            const response = await hybrisProxyHttp.post(`/api/users/${this.createUserNameForUrl(username)}/carts`, undefined, {
                params: {
                    oldCartId: basketId,
                    chl: channelHelper.getChannel()
                },
                headers: this.getHybrisAuthHeaders()
            });

            return response.data;
        } catch (error) {
            const errorSource = 'hybris.repository:transferBasket';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async reserveCart(username: string, basketId: string): Promise<void> {
        try {
            await hybrisProxyHttp.post(`api/users/${username}/carts/${basketId}/confirmReservation`, null, {
                params: {
                    chl: channelHelper.getChannel(),
                    baseSiteId: 'cmsSofaworksSite',
                    userId: username,
                    cartId: basketId
                },
                headers: this.getHybrisAuthHeaders()
            });
        } catch (error) {
            const errorSource = 'hybris.repository:reserveCart';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async placeOrder(username: string, order: OrderPayload, basketId: string): Promise<PlacedOrderModel> {
        try {
            const response = await hybrisHttp.post(`/v2/cmsSofaworksSite/users/${username}/orders`, order, {
                params: {
                    storeParam: channelHelper.getChannel(),
                    chl: channelHelper.getChannel(),
                    cartId: basketId,
                    channel: 'desktop'
                },
                headers: this.getHybrisAuthHeaders()
            });

            return plainToClass(PlacedOrderModel, response.data);
        } catch (error) {
            const errorSource = 'hybris.repository:placeOrder';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async getOrders(username: string): Promise<PlacedOrderModel[]> {
        try {
            const response = await hybrisHttp.get(`/v2/cmsSofaworksSite/users/${username}/orders`, {
                params: {
                    storeParam: channelHelper.getChannel(),
                    chl: channelHelper.getChannel()
                },
                headers: this.getHybrisAuthHeaders()
            });

            return plainToClass(PlacedOrderModel, response.data?.orders as any[] || []);
        } catch (error) {
            const errorSource = 'hybris.repository:getOrders';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async getOrder(username: string, orderCode: string): Promise<PlacedOrderModel> {
        try {
            const response = await hybrisHttp.get(`/v2/cmsSofaworksSite/users/${username}/orders/${orderCode}`, {
                params: {
                    fields: HybrisFieldTypes.Full,
                    chl: channelHelper.getChannel()
                },
                headers: this.getHybrisAuthHeaders()
            });

            return plainToClass(PlacedOrderModel, response.data);
        } catch (error) {
            const errorSource = 'hybris.repository:getOrder';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async setupPaypalPayment(username: string, sessionId: string, amount: number): Promise<string> {
        try {
            const response = await hybrisProxyHttp.post(`/api/payments/setup/${username}/carts`, undefined, {
                params: {
                    sessionId,
                    paymentAmount: amount
                }
            });

            return response.data;
        } catch (error) {
            const errorSource = 'hybris.repository:setupPaypalPayment';
            if (isApiError(error)) {
                const apiError = new BasketApiErrorModel(error, errorSource);

                if (error.response?.status === DATA_VALIDATION_ERROR_STATUS_CODE && error.response?.data?.errors) {
                    throw new BasketErrorModel(`Sorry your payment was declined. ${error.response?.data.errors[0].clientMessage}`, undefined, errorSource, undefined, apiError);
                }

                throw new BasketErrorModel(`Unable to get details from paypal ${error.response?.status ?? ''}`, undefined, errorSource, undefined, apiError);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async executePaypalPayment(paymentId: string, payerId: string): Promise<PaypalSuccessfulPaymentResponseModel> {
        try {
            const response = await hybrisProxyHttp.post('/api/payments/execute', { payerId, paymentId });

            let result = new PaypalSuccessfulPaymentResponseModel();
            result.id = response.data.id;
            result.payerId = response.data.payer.payer_info.payer_id;
            result.payerEmail = response.data.payer.payer_info.email;
            result.state = response.data.state;
            result.invoiceNumber = response.data.transactions[0].invoice_number;

            return result;
        } catch (error) {
            const errorSource = 'hybris.repository:executePaypalPayment';
            if (isApiError(error)) {
                const apiError = new BasketApiErrorModel(error, errorSource);

                if (error.response?.data?.name === PAYPAL_DECLINED_ERROR_CODE) {
                    throw new BasketPaymentErrorModel('Your PayPal payment has been declined. Please try again or select a different payment method.', PAYPAL_DECLINED_ERROR_CODE, errorSource, apiError);
                }

                throw new BasketErrorModel('Sorry, there was an error taking the payment from PayPal. Please try again or select a different payment method.', undefined, errorSource, undefined, apiError);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async saveCurrentPage(currentPage: string): Promise<void> {
        try {
            const sessionId = userHelper.getSessionId();
            const user = userHelper.getUser();

            if (sessionId && user) {
                const username = this.createUserNameForUrl(user.username);

                await hybrisProxyHttp.put(`/api/users/${username}/sessions/${sessionId}/currentPage`,
                    undefined,
                    {
                        params: {
                            value: currentPage
                        }
                    });
            }
        } catch (error) {
            const errorSource = 'hybris.repository:saveCurrentPage';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async saveFinanceSubmissionDate(username: string, sessionId: string): Promise<void> {
        try {
            await hybrisProxyHttp.put(`/api/users/${username}/sessions/${sessionId}/financeSubmissionDate`,
                undefined,
                {
                    params: {
                        value: new Date().getTime()
                    }
                });
        } catch (error) {
            const errorSource = 'hybris.repository:saveFinanceSubmissionDate';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async getFinanceApplicationStatus(username: string | null, applicationId: string): Promise<FinanceApplicationStatusModel | null> {
        try {
            const response = await hybrisHttp.get(`/v2/cmsSofaworksSite/users/${this.createUserNameForUrl(username)}/${applicationId}/finance`, {
                params: {
                    fields: HybrisFieldTypes.Basic,
                    chl: channelHelper.getChannel()
                },
                headers: this.getHybrisAuthHeaders()
            });

            // cloned from old code - could the statusId be not equal to application id??
            if (response.data?.id !== applicationId) {
                return null;
            }

            return plainToClass(FinanceApplicationStatusModel, response.data);
        } catch (error) {
            const errorSource = 'hybris.repository:getFinanceApplicationStatus';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async addProductsBatch(username: string | null, basketId: string | null, items: AddToCartItemModel[]): Promise<AddToCartResultModel> {
        try {
            const url = `/api/users/${this.createUserNameForUrl(username)}/carts/${basketId}/entries/batch`;
            const response = await hybrisProxyHttp.post(url, { Items: items }, {
                params: {
                    chl: channelHelper.getChannel()
                },
                headers: this.getHybrisAuthHeaders()
            });

            return plainToClass(AddToCartResultModel, response.data);
        } catch (error) {
            const errorSource = 'hybris.repository:addProductsBatch';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    private async getBasketAddons(username: string | null, basketId: string, type: 'sofaShields' | 'careKits'): Promise<BasketAddonModel[] | BasketCareKitAddonModel[]> {
        const response = await hybrisProxyHttp.get(`/api/users/${this.createUserNameForUrl(username)}/carts/${basketId}/addons/${type}`, {
            headers: this.getHybrisAuthHeaders()
        });

        return type === 'careKits'
            ? plainToClass(BasketCareKitAddonModel, response.data.products as any[] || [])
            : plainToClass(BasketAddonModel, response.data.products as any[] || []);
    }

    private getHybrisAuthHeaders(): any {
        let user = userHelper.getUser();
        let staffUser = userHelper.getStaffUser();

        let headers: any = {};

        if (staffUser) {
            headers.Authorization = staffUser.token;
        } else if (user?.username) {
            headers.Authorization = user.token;
        }

        return headers;
    }

    private createUserNameForUrl(username: string | null): string {
        return (username ?? 'anonymous').toLowerCase();
    }

    private getHybrisErrorsListFormResponse(error: any, formName: string, innerError: BasketApiErrorModel): BasketHybrisFormErrorModel[] {
        return error.response?.data?.errors?.map((e: { message: string; reason: string; subject: string; type: string }) =>
            new BasketHybrisFormErrorModel(e.message, formName, e.type, e.subject, e.reason, innerError)) ?? [];
    }

    // eslint-disable-next-line sonarjs/cognitive-complexity
    private parseCustomerDetails(data: CustomerDetailsResponseModel): HybrisCustomerDetailsModel {
        let res = new HybrisCustomerDetailsModel();
        if (data.customerId && data.customerId.length > 0) {
            res.customerId = data.customerId;
        }

        res.email = data.name;

        // parse algorithm cloned from old code
        if (data.defaultAddress) {
            res.isCustomerInfoExists = true;

            res.firstName = data.defaultAddress.firstName && data.defaultAddress.firstName.length > 0 ? data.defaultAddress.firstName : null;
            res.surname = data.defaultAddress.lastName && data.defaultAddress.lastName.length > 0 ? data.defaultAddress.lastName : null;
            res.phone = data.defaultAddress.cellPhone && data.defaultAddress.cellPhone.length > 0 ? data.defaultAddress.cellPhone : null;
            res.title = data.defaultAddress.titleCode && data.defaultAddress.titleCode.length > 0 ? data.defaultAddress.titleCode.toLowerCase() : null;
            res.deliveryAddress = convertHybrisAddressResponseToAddressModel(data.defaultAddress);
        }

        if (data.defaultBillingAddress) {
            res.billingAddress = convertHybrisAddressResponseToAddressModel(data.defaultBillingAddress);
        }

        return res;
    }

    private createUpdateCustumerDetailsRequestModel(customerDetails: HybrisCustomerDetailsModel, address: AddressModel, isBillingAddress: boolean): any {
        return {
            billingAddress: false,
            contactAddress: false,
            defaultAddress: false,
            email: customerDetails.email!.toLowerCase(),
            cellPhone: customerDetails.phone,
            firstName: customerDetails.firstName,
            lastName: customerDetails.surname,
            shippingAddress: !isBillingAddress,
            titleCode: customerDetails.title,
            country: {
                isocode: 'GB',
                name: 'United Kingdom'
            },
            building: address.building,
            apartment: address.flat,
            line1: address.number ? (address.number + ' ' + address.street) : address.street,
            line2: address.town,
            town: address.town,
            district: address.county,
            postalCode: address.postcode!.code.toUpperCase()
        };
    }
}

export const hybris = new HybrisRepository();

interface CustomerDetailsResponseModel {
    customerId?: string;
    uid: string;
    name: string;
    defaultAddress?: {
        firstName?: string;
        lastName?: string;
        cellPhone?: string;
        titleCode?: string;
        line1?: string;
        line2?: string;
        building?: string;
        apartment?: string;
        town?: string;
        district?: string;
        postalCode?: string;
    };
    defaultBillingAddress?: {
        firstName?: string;
        lastName?: string;
        cellPhone?: string;
        titleCode?: string;
        line1?: string;
        line2?: string;
        building?: string;
        apartment?: string;
        town?: string;
        district?: string;
        postalCode?: string;
    };
}
